import { type FC } from 'react';
import { graphql, usePaginationFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';

import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { Header } from 'dibs-elements/exports/Header.tsx';
import { NftItemTile } from 'dibs-nft-item-tile/exports/NftItemTile.ts';
import { NftHeartPillProvider } from 'dibs-nft-heart-pill/exports/NftHeartPillProvider.ts';
import { useCurrencyStore } from 'dibs-buyer-layout/exports/currencyStore.ts';
import { NftProfileSectionLoadMore } from './NftProfileSectionLoadMore.tsx';
import { trackPdpImpression, trackPdpRedirect } from './nftCollectorTracking.ts';

import styles from './NftProfileSections.scss';

import { type NftProfileSectionAuctions_viewer$key as ViewerRef } from './__generated__/NftProfileSectionAuctions_viewer.graphql.ts';
import { type NftProfileSectionAuctionsPaginationQuery as PaginationQuery } from './__generated__/NftProfileSectionAuctionsPaginationQuery.graphql.ts';

type Props = {
    isMobile: boolean;
    viewer: ViewerRef;
};

export const NftProfileSectionAuctions: FC<Props> = ({ isMobile, viewer: viewerRef }) => {
    const { currency } = useCurrencyStore();
    const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment<
        PaginationQuery,
        ViewerRef
    >(
        graphql`
            fragment NftProfileSectionAuctions_viewer on Viewer
            @refetchable(queryName: "NftProfileSectionAuctionsPaginationQuery") {
                nftsForSale: nftItemSearch(
                    auctionSellerProfileUsernames: [$username]
                    auctionSellerAddresses: [$walletAddress]
                    first: $liveAuctionsFirst
                    after: $liveAuctionsCursor
                    excludeOwnerCreatedItems: true
                ) @connection(key: "NftProfileSectionAuctions_nftsForSale") {
                    edges {
                        node {
                            serviceId
                            metadata {
                                ... on NftDigitalArtMetadata {
                                    name
                                }
                            }
                            mostRecentAuction(excludeCanceled: true) {
                                ...NftItemTile_nftAuction
                            }
                            ...NftItemTile_nftItem
                        }
                    }
                }
                ...NftHeartPillProvider_viewer
                ...NftItemTile_viewer
            }
        `,
        viewerRef
    );

    return (
        <div className={styles.auctionSectionContainer}>
            <div className={styles.auctionSectionHeader}>
                <Header
                    title={
                        <FormattedMessage
                            id="nftProfile.auctionSection.forSale.title"
                            defaultMessage="For Sale"
                        />
                    }
                    htmlElementType="h2"
                />
            </div>

            <NftHeartPillProvider
                viewer={data}
                nftItemIds={(data.nftsForSale?.edges || [])
                    .map(edge => edge?.node?.serviceId || null)
                    .filter(filterNulls)}
            >
                <ul className={styles.auctionItems}>
                    {data.nftsForSale?.edges?.map((edge, index) => {
                        const nftItem = edge?.node;

                        if (!nftItem || !nftItem.mostRecentAuction) {
                            return null;
                        }

                        return (
                            <li className={styles.itemTileWrapper} key={index}>
                                <NftItemTile
                                    tileType="COLLECTOR_TILE"
                                    nftItem={nftItem}
                                    nftAuction={nftItem.mostRecentAuction}
                                    fireTracking={trackPdpRedirect(
                                        nftItem.serviceId,
                                        nftItem.metadata?.name,
                                        index,
                                        'COLLECTION_TAB'
                                    )}
                                    fireImpressionTracking={trackPdpImpression(
                                        nftItem.serviceId,
                                        nftItem.metadata?.name,
                                        index,
                                        'COLLECTION_TAB'
                                    )}
                                    key={nftItem.serviceId}
                                    viewer={data}
                                    currency={currency}
                                    gaLabel="NFT-Collector"
                                    isMobile={isMobile}
                                />
                            </li>
                        );
                    })}
                </ul>
            </NftHeartPillProvider>

            <NftProfileSectionLoadMore
                hasNext={hasNext}
                isLoadingNext={isLoadingNext}
                onClick={() => loadNext(20)}
            />
        </div>
    );
};
